import { forwardRef } from 'react'
import { HStack, Button, Text, Box, Flex } from '@chakra-ui/react'
import type { MetaData, PaginationLink } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  metas: MetaData
  onClick: (url: string | null) => void
  className?: string
}

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(({ metas, onClick }, ref) => {
  const colors = useThemeColors()
  const { total, links, per_page: perPage, current_page: currentPage } = metas

  const textColor = colors.text
  const scrollbarThumb = colors.progressBg
  const scrollbarThumbHover = colors.textMuted
  const ellipsisColor = colors.text

  const goToPage = (url: string | null) => {
    if (url) onClick(url)
  }

  const getPageInfo = () => {
    const start = (currentPage - 1) * perPage + 1
    const end = Math.min(currentPage * perPage, total)
    return `Showing ${start} to ${end} of ${total.toLocaleString()} entries`
  }

  return (
    <Box ref={ref} w="100%" overflowX="auto">
      <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={2}>
        <Text fontSize="xs" color={textColor} whiteSpace="nowrap" mb={{ base: 1, md: 0 }} textAlign={{ base: 'center', md: 'left' }}>
          {getPageInfo()}
        </Text>

        <Box
          overflowX="auto"
          w={{ base: '100%', md: 'auto' }}
          css={{
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: scrollbarThumb,
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: scrollbarThumbHover,
            },
          }}
        >
          <HStack spacing={1} justify={{ base: 'center', md: 'flex-end' }} minW="fit-content">
            {links?.map((link: PaginationLink, index: number) => {
              const isDisabled = !link.url
              const isActive = link.active
              const isEllipsis = link.label === '...'

              if (isEllipsis) {
                return (
                  <Box key={index} px={{ base: 1, md: 2 }} py={1}>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color={ellipsisColor}>
                      {link.label}
                    </Text>
                  </Box>
                )
              }

              return (
                <Button
                  key={index}
                  size={{ base: 'xs', md: 'sm' }}
                  variant="outline"
                  isDisabled={isDisabled}
                  onClick={() => goToPage(link.url)}
                  minW={{ base: '32px', md: '40px' }}
                  h={{ base: '28px', md: '32px' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 3 }}
                  flexShrink={0}
                  bg={isActive ? colors.primary : 'transparent'}
                  color={isActive ? 'white' : colors.text}
                  borderColor={isActive ? colors.primary : colors.border}
                  _hover={{
                    bg: isActive ? colors.primaryHover : colors.sectionBg,
                    borderColor: colors.primary,
                  }}
                  _active={{
                    bg: colors.primaryHover,
                  }}
                >
                  {(() => {
                  const plain = link.label.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '').trim()

                  if (plain === 'prev') return <ChevronLeft size={16} />
                  if (plain === 'next') return <ChevronRight size={16} />

                  return plain
                })()}
                </Button>
              )
            })}
          </HStack>
        </Box>
      </Flex>
    </Box>
  )
})

Pagination.displayName = 'Pagination'


